/**
 * Модель черновика сообщения (Draft) - то, что редактирует панель и хранится в шаблонах.
 * Она ближе к UI, чем к Discord API: у каждого элемента есть стабильный id,
 * а действия кнопок описаны декларативно и превращаются в custom_id при отправке.
 */

export type MessageMode = 'classic' | 'v2';

export type ButtonStyleName = 'primary' | 'secondary' | 'success' | 'danger' | 'link';

/** Что делает интерактивный элемент при нажатии. */
export type DAction =
  | { type: 'none' }
  | { type: 'template'; templateId: string }
  | { type: 'role'; roleId: string; mode: 'toggle' | 'add' | 'remove' }
  /** custom_id из чужого сообщения - сохраняем как есть, бот его не обработает */
  | { type: 'raw'; customId: string };

export interface DButton {
  id: string;
  type: 'button';
  style: ButtonStyleName;
  label: string;
  /** юникод-эмодзи или <:name:id> / <a:name:id> */
  emoji: string;
  url: string;
  disabled: boolean;
  action: DAction;
}

export interface DSelectOption {
  id: string;
  label: string;
  description: string;
  emoji: string;
  isDefault: boolean;
  action: DAction;
  /** исходное value для raw-действий */
  rawValue?: string;
}

export interface DSelect {
  id: string;
  type: 'select';
  placeholder: string;
  minValues: number;
  maxValues: number;
  disabled: boolean;
  options: DSelectOption[];
  /** исходный custom_id для чужих меню */
  rawCustomId?: string;
}

export interface DActionRow {
  id: string;
  type: 'row';
  items: (DButton | DSelect)[];
}

export interface DField {
  id: string;
  name: string;
  value: string;
  inline: boolean;
}

export interface DEmbed {
  id: string;
  color: number | null;
  title: string;
  url: string;
  description: string;
  author: { name: string; url: string; iconUrl: string };
  footer: { text: string; iconUrl: string };
  /** ISO-строка или null */
  timestamp: string | null;
  image: string;
  thumbnail: string;
  fields: DField[];
}

export interface DPollAnswer {
  id: string;
  text: string;
  emoji: string;
}

export interface DPoll {
  question: string;
  answers: DPollAnswer[];
  durationHours: number;
  multiselect: boolean;
}

export interface DAttachment {
  id: string;
  /** имя файла в сообщении, по нему ссылаются attachment://name */
  name: string;
  /** https://... или upload://<id>/<name> (загрузка через панель) */
  source: string;
  spoiler: boolean;
  description: string;
}

/* ---------- Components V2 ---------- */

export interface DText {
  id: string;
  type: 'text';
  content: string;
}

export interface DThumbnail {
  type: 'thumbnail';
  url: string;
  description: string;
  spoiler: boolean;
}

export interface DSection {
  id: string;
  type: 'section';
  texts: DText[];
  accessory: DThumbnail | DButton;
}

export interface DMediaItem {
  id: string;
  url: string;
  description: string;
  spoiler: boolean;
}

export interface DGallery {
  id: string;
  type: 'gallery';
  items: DMediaItem[];
}

export interface DFile {
  id: string;
  type: 'file';
  /** имя вложения из draft.attachments */
  attachment: string;
  spoiler: boolean;
}

export interface DSeparator {
  id: string;
  type: 'separator';
  divider: boolean;
  spacing: 1 | 2;
}

export type DV2Child = DText | DSection | DGallery | DFile | DSeparator | DActionRow;

export interface DContainer {
  id: string;
  type: 'container';
  accent: number | null;
  spoiler: boolean;
  children: DV2Child[];
}

export type DV2Top = DContainer | DV2Child;

export interface DProfile {
  /** имя и аватар при отправке через вебхук */
  username: string;
  avatarUrl: string;
}

export interface DOptions {
  /** @silent - без уведомлений */
  silent: boolean;
  /** скрыть превью ссылок */
  suppressEmbeds: boolean;
  mentions: { everyone: boolean; roles: boolean; users: boolean };
}

export interface Draft {
  version: 1;
  mode: MessageMode;
  content: string;
  embeds: DEmbed[];
  rows: DActionRow[];
  v2: DV2Top[];
  poll: DPoll | null;
  attachments: DAttachment[];
  profile: DProfile;
  options: DOptions;
}

/** Куда сохранить правку существующего сообщения. */
export interface EditTarget {
  channelId: string;
  messageId: string;
  /** id ветки, если сообщение в ветке/посте форума */
  threadId?: string;
  via: 'bot' | 'webhook';
  webhookId?: string;
  wasV2: boolean;
}

/* ---------- Минимальные типы Discord API, которые нам нужны ---------- */

export interface ApiEmoji {
  id?: string | null;
  name?: string | null;
  animated?: boolean;
}

export interface ApiEmbed {
  type?: string;
  title?: string;
  description?: string;
  url?: string;
  color?: number;
  timestamp?: string;
  footer?: { text: string; icon_url?: string };
  image?: { url: string };
  thumbnail?: { url: string };
  author?: { name: string; url?: string; icon_url?: string };
  fields?: { name: string; value: string; inline?: boolean }[];
  // поля, которые Discord добавляет сам
  [k: string]: unknown;
}

export interface ApiComponent {
  type: number;
  id?: number;
  [k: string]: any;
}

export interface ApiAttachment {
  id: string;
  filename: string;
  url: string;
  size?: number;
  content_type?: string;
  description?: string;
  flags?: number;
}

export interface ApiUser {
  id: string;
  username: string;
  global_name?: string | null;
  avatar?: string | null;
  bot?: boolean;
}

export interface ApiPoll {
  question: { text?: string };
  answers: { answer_id?: number; poll_media: { text?: string; emoji?: ApiEmoji } }[];
  allow_multiselect?: boolean;
  expiry?: string | null;
  duration?: number;
}

export interface ApiMessage {
  id: string;
  channel_id: string;
  guild_id?: string;
  author: ApiUser;
  content: string;
  embeds: ApiEmbed[];
  components?: ApiComponent[];
  attachments?: ApiAttachment[];
  flags?: number;
  webhook_id?: string;
  application_id?: string;
  poll?: ApiPoll;
  timestamp?: string;
}

export const MessageFlags = {
  SuppressEmbeds: 1 << 2,
  Ephemeral: 1 << 6,
  SuppressNotifications: 1 << 12,
  IsComponentsV2: 1 << 15,
} as const;

export const ComponentType = {
  ActionRow: 1,
  Button: 2,
  StringSelect: 3,
  TextInput: 4,
  UserSelect: 5,
  RoleSelect: 6,
  MentionableSelect: 7,
  ChannelSelect: 8,
  Section: 9,
  TextDisplay: 10,
  Thumbnail: 11,
  MediaGallery: 12,
  File: 13,
  Separator: 14,
  Container: 17,
  Label: 18,
} as const;

export const ButtonStyle: Record<ButtonStyleName, number> = {
  primary: 1,
  secondary: 2,
  success: 3,
  danger: 4,
  link: 5,
};
